import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { formatDate } from '../../utils/Utils';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: grid;
  place-items: center;
  background: rgba(0, 66, 66, 0.35);
`;

const Card = styled.div`
  width: 560px;
  max-width: 92vw;
  max-height: 86vh;
  overflow-y: auto;
  background: #fff;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  padding: 20px;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 12px;
`;

const CloseBtn = styled.button`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;

  &:hover {
    background: rgba(2, 145, 239, 0.08);
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin: 18px 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};

  &:first-of-type {
    margin-top: 4px;
  }
`;

const SectionTotal = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};
  white-space: nowrap;
`;

const SectionNote = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 6px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};

  th {
    text-align: left;
    padding: 6px 8px;
    color: ${({ theme }) => theme.colors.text.dark.little};
    font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
    border-bottom: 1px solid #e8ecf0;
  }

  td {
    padding: 6px 8px;
    color: ${({ theme }) => theme.colors.text.primary};
    border-bottom: 1px solid #f1f4f7;
  }
`;

const Empty = styled.div`
  padding: 8px 4px 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

// Bakiye ekseni: annual → yıllık toplama girer; own → yıllık izinden bağımsız
// kendi hakkı; none → hakedişi/bakiyesi olmayan tür (yalnız kullanım izlenir).
// Task 3 öncesi tüm Variations örtük olarak "annual" sayılırdı (IsPaid &&
// !IsHourly süzgeci) — balanceScope taşımayan eski veri/test satırları da
// aynı varsayılanla (annual) gruplanır, geriye dönük kırılma olmasın diye.
const BALANCE_SCOPE = { ANNUAL: 'annual', OWN: 'own', NONE: 'none' };

function scopeOf(variation) {
  return variation.balanceScope || BALANCE_SCOPE.ANNUAL;
}

function unitLabelOf(variation, t) {
  return (variation.isHourly ? t('leaveBalanceReport.unitHour') : t('leaveBalanceReport.unitDay')).toLowerCase();
}

function withUnit(value, variation, t) {
  return `${Number(value ?? 0)} ${unitLabelOf(variation, t)}`;
}

// Düzeltme turu 2, bulgu A: olay başına hak eden tür (babalık/evlilik/ölüm/
// doğum) yıllık hakediş kuralı TAŞIMAZ — backend entitlement 0 döner ve
// `remaining = 0 − kullanılan` NEGATİF basılırdı. Anlamlı tavan olay başına
// instanceLimit'tir; "hak ediş" hücresi onu yazar, "kalan" hücresi — none
// ekseninde olduğu gibi — BOŞ bırakılır.
const PER_EVENT = 'perEvent';

function isPerEvent(variation) {
  return variation.entitlementBasis === PER_EVENT;
}

function perEventLabel(variation, t) {
  return variation.instanceLimit === null || variation.instanceLimit === undefined
    ? t('leaveBalanceReport.perEventNoLimit')
    : t('leaveBalanceReport.perEventLimit', {
      limit: Number(variation.instanceLimit),
      unit: unitLabelOf(variation, t),
    });
}

// Aylık birikim / devir tavanı olan tür (bkz. backend Common/LeaveAccrualEngine.cs): bir
// sonraki birikim, bir sonraki yıl dönümünde nakde çevrilecek tahmini gün ve geçmiş yıl
// dönümlerinde nakde çevrilen toplam. Yıllık + tavansız türde alanlar boştur.
function hasAccrualInfo(v) {
  return (v.accrualFrequency === 'monthly' && !!v.nextAccrualDate)
    || Number(v.projectedPayout) > 0
    || Number(v.paidOut) > 0;
}

// Salt-okunur tür kırılımı — satır tıklamasıyla açılır (DataTable sarmalayıcısı
// expandableRows desteklemediği için modal; OB ekip kronolojisi modali emsali).
//
// Task 11B: BalanceScope'a geçince Variations ücretsiz/saatlik türleri de
// taşımaya başladı; modal üç bölüme ayrılır — Yıllık İzin (başlık toplamını
// oluşturan annual satırlar), Diğer Haklarınız (own — yıllık izinden düşülmez)
// ve Bakiyesiz İzinler (none — yalnız kullanılan, negatif kalan basılmaz).
export default function BreakdownModal({ row, onClose }) {
  const { t, i18n } = useTranslation();
  if (!row) return null;

  const variations = row.variations || [];
  const annualRows = variations.filter((v) => scopeOf(v) === BALANCE_SCOPE.ANNUAL);
  const ownRows = variations.filter((v) => scopeOf(v) === BALANCE_SCOPE.OWN);
  const noneRows = variations.filter((v) => scopeOf(v) === BALANCE_SCOPE.NONE);
  const hasAnyVariation = variations.length > 0;
  const accrualRows = variations.filter((v) => scopeOf(v) !== BALANCE_SCOPE.NONE && hasAccrualInfo(v));

  return (
    <Overlay onClick={onClose} role="dialog" aria-label={t('leaveBalanceReport.breakdownTitle')}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Head>
          <Title>{t('leaveBalanceReport.breakdownTitle')}</Title>
          <CloseBtn type="button" onClick={onClose} aria-label={t('leaveBalanceReport.close')}>
            <X size={16} />
          </CloseBtn>
        </Head>
        <Sub>{row.fullName}</Sub>

        {!hasAnyVariation ? (
          <Empty>{t('leaveBalanceReport.noVariations')}</Empty>
        ) : (
          <>
            <SectionTitle>
              <span>{t('leaveBalanceReport.annualSection')}</span>
              {row.remaining !== undefined && row.remaining !== null && (
                <SectionTotal data-testid="breakdown-total">
                  {t('leaveBalanceReport.remaining')}: {Number(row.remaining)} {t('leaveBalanceReport.unitDay').toLowerCase()}
                </SectionTotal>
              )}
            </SectionTitle>
            {annualRows.length ? (
              <Table>
                <thead>
                  <tr>
                    <th>{t('leaveBalanceReport.leaveType')}</th>
                    <th>{t('leaveBalanceReport.entitlement')}</th>
                    <th>{t('leaveBalanceReport.used')}</th>
                    <th>{t('leaveBalanceReport.pending')}</th>
                    <th>{t('leaveBalanceReport.remaining')}</th>
                  </tr>
                </thead>
                <tbody>
                  {annualRows.map((v) => (
                    <tr key={v.leaveVariationId || v.name}>
                      <td>{v.name}</td>
                      <td>{withUnit(v.entitlement, v, t)}</td>
                      <td>{withUnit(v.used, v, t)}</td>
                      <td>{withUnit(v.pending, v, t)}</td>
                      <td>{withUnit(v.remaining, v, t)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Empty>{t('leaveBalanceReport.noVariations')}</Empty>
            )}

            {ownRows.length > 0 && (
              <>
                <SectionTitle>
                  <span>{t('leaveBalanceReport.otherEntitlements')}</span>
                </SectionTitle>
                <SectionNote>{t('leaveBalanceReport.otherEntitlementsNote')}</SectionNote>
                <Table>
                  <thead>
                    <tr>
                      <th>{t('leaveBalanceReport.leaveType')}</th>
                      <th>{t('leaveBalanceReport.entitlement')}</th>
                      <th>{t('leaveBalanceReport.used')}</th>
                      <th>{t('leaveBalanceReport.pending')}</th>
                      <th>{t('leaveBalanceReport.remaining')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownRows.map((v) => (
                      <tr key={v.leaveVariationId || v.name}>
                        <td>{v.name}</td>
                        <td data-testid={`own-entitlement-${v.leaveVariationId || v.name}`}>
                          {isPerEvent(v) ? perEventLabel(v, t) : withUnit(v.entitlement, v, t)}
                        </td>
                        <td>{withUnit(v.used, v, t)}</td>
                        <td>{withUnit(v.pending, v, t)}</td>
                        <td data-testid={`own-remaining-${v.leaveVariationId || v.name}`}>
                          {isPerEvent(v) ? null : withUnit(v.remaining, v, t)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {accrualRows.length > 0 && (
              <>
                <SectionTitle>
                  <span>{t('leaveBalanceReport.accrualSection')}</span>
                </SectionTitle>
                <Table>
                  <thead>
                    <tr>
                      <th>{t('leaveBalanceReport.leaveType')}</th>
                      <th>{t('leaveBalanceReport.nextAccrual')}</th>
                      <th>{t('leaveBalanceReport.projectedPayout')}</th>
                      <th>{t('leaveBalanceReport.paidOut')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accrualRows.map((v) => {
                      const key = v.leaveVariationId || v.name;
                      return (
                        <tr key={key}>
                          <td>{v.name}</td>
                          <td data-testid={`next-accrual-${key}`}>
                            {v.accrualFrequency === 'monthly' && v.nextAccrualDate
                              ? `${formatDate(v.nextAccrualDate, i18n.language)} · +${withUnit(v.nextAccrualDays, v, t)}`
                              : '—'}
                          </td>
                          <td data-testid={`projected-payout-${key}`}>
                            {Number(v.projectedPayout) > 0 && v.nextAnniversary
                              ? `${formatDate(v.nextAnniversary, i18n.language)} · ${withUnit(v.projectedPayout, v, t)}`
                              : '—'}
                          </td>
                          <td data-testid={`paid-out-${key}`}>
                            {Number(v.paidOut) > 0 ? withUnit(v.paidOut, v, t) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </>
            )}

            {noneRows.length > 0 && (
              <>
                <SectionTitle>
                  <span>{t('leaveBalanceReport.noBalanceLeaves')}</span>
                </SectionTitle>
                <Table>
                  <thead>
                    <tr>
                      <th>{t('leaveBalanceReport.leaveType')}</th>
                      <th>{t('leaveBalanceReport.used')}</th>
                      <th>{t('leaveBalanceReport.remaining')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {noneRows.map((v) => (
                      <tr key={v.leaveVariationId || v.name}>
                        <td>{v.name} ({unitLabelOf(v, t)})</td>
                        <td data-testid={`none-used-${v.leaveVariationId || v.name}`}>{Number(v.used ?? 0)}</td>
                        <td data-testid={`none-remaining-${v.leaveVariationId || v.name}`} />
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </Card>
    </Overlay>
  );
}
